import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CButton, CCard, CCardBody } from "@coreui/react";
import { JobStatusBadge } from "@src/components/JobStatusBadge";
import { PaginationControls } from "@src/components/PaginationControls";
import { useEntityFilters } from "@src/hooks/useEntityFilters";
import { useOptimizations } from "@src/hooks/redux";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useApiPagination } from "@src/hooks/usePagination";
import { useToastActions } from "@src/hooks/useToasts";
import { getEntityConfig } from "@src/services/config/entityConfigs";
import { PAGINATION_STALLED_MESSAGE } from "@src/utils/constants";
import { CDataTable } from "@tradingdroid/frontends-components";
import type { ApiStudy } from "@tradingdroid/frontends-types";
import dayjs from "dayjs";

/**
 * OptimizationsListPage - Displays optimization studies with job metadata
 *
 * ## Data Flow (supports two scenarios)
 *
 * ### Scenario 1: Backend embed NOT available (current)
 * The API returns studies without `job` data. We hydrate studies client-side:
 * 1. API returns: { study_id, study_name } (no job)
 * 2. Extract job_id from study_name pattern: "job_{id}_{strategy}" -> id
 * 3. Look up job in Redux (fetched at app bootstrap)
 * 4. Merge job data into study for display
 *
 * ### Scenario 2: Backend embed available (after B_fix-optimization PR merged)
 * The API returns studies WITH `job` data via ?embed=job parameter:
 * 1. API returns: { study_id, study_name, job: { id, description, status_name, ... } }
 * 2. Hydration code detects `opt.job` exists and returns study as-is
 * 3. No client-side lookup needed
 *
 * ## Code ready for both scenarios
 * - The `embed: "job"` param is already passed to API (ignored if backend doesn't support it)
 * - Hydration checks `if (opt.job) return opt` first, so backend data takes priority
 * - Falls back to Redux job lookup only when backend embed not available
 *
 * ## After backend PR is merged
 * No code changes needed! The hydration will simply short-circuit since opt.job will exist.
 * Optional cleanup: Remove Redux job hydration code once backend is stable.
 */
export const OptimizationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { showJsonModal } = useJsonModal();
  const { optimizations, setOptimizations, removeOptimization } = useOptimizations();

  // Use new unified filter/pagination hook (no filters for now, just pagination)
  const { pagination, setPerPage, nextPage, prevPage } = useEntityFilters("optimizations");

  // Get entity config to use its pageSize
  const defaultPageSize = getEntityConfig("studies").pageSize;

  // Unified API hook: handles initial fetch + pagination with single loading state
  const { loading, fetchNextPage, hasMore, stalled } = useApiPagination({
    entityName: "studies",
    currentItems: optimizations,
    setItems: setOptimizations,
  });

  // For now, no client-side filtering - just use all optimizations (no intermediate variable)

  // Calculate current page items
  const pageOptimizations = React.useMemo(() => {
    const start = pagination.page * pagination.perPage;
    const end = start + pagination.perPage;
    return optimizations.slice(start, end);
  }, [optimizations, pagination.page, pagination.perPage]);

  const handleDelete = async (optimization: ApiStudy) => {
    const { confirmed } = await confirmModal(
      `Are you sure you want to delete optimization study "${optimization.study_name}"?`,
      { title: "Delete optimization" },
    );
    if (!confirmed) return;

    try {
      // Note: API deletion not implemented yet, just remove from Redux for now
      removeOptimization(optimization.study_id);
      showToast({ message: "Optimization deleted successfully", type: "success" });
    } catch (error) {
      console.error("Failed to delete optimization:", error);
      showToast({ message: "Failed to delete optimization", type: "danger" });
    }
  };

  // Define table columns (using basic ApiStudy data only)
  const optimizationTableColumns = [
    {
      columnKey: "id",
      columnLabel: "ID",
      columnStyle: { width: "70px" },
      cellRender: (opt: ApiStudy) => (
        <div className="d-flex align-items-center gap-1">
          <Link className="td-idLink" to={`/study/${opt.study_id}`}>
            {opt.study_id}
          </Link>
          <CButton
            color="info"
            variant="ghost"
            size="sm"
            onClick={() => showJsonModal(opt, `Optimization #${opt.study_id}`)}
            title="View full record"
            style={{ padding: "0 4px" }}
          >
            ℹ️
          </CButton>
        </div>
      ),
    },
    {
      columnKey: "name",
      columnLabel: "Name / Study",
      cellRender: (opt: ApiStudy) => {
        const userProvidedName = opt.job?.description;
        const displayName = userProvidedName || `Study ${opt.study_id}`;
        return (
          <div>
            <div>{displayName}</div>
            <small className="text-muted">{opt.study_name}</small>
          </div>
        );
      },
    },
    {
      columnKey: "status",
      columnLabel: "Status",
      columnStyle: { width: "120px" },
      cellRender: (opt: ApiStudy) => (
        <JobStatusBadge status={opt.job?.status_name} runningState={opt.job?.running_state} />
      ),
    },
    {
      columnKey: "created",
      columnLabel: "Created",
      columnStyle: { width: "140px" },
      cellRender: (opt: ApiStudy) => {
        // Use job.create_ts as primary source
        const createdTs = opt.job?.create_ts || opt.created_ts;
        if (createdTs) {
          return dayjs(createdTs).format("YYYY-MM-DD HH:mm");
        }
        return "N/A";
      },
    },
    {
      columnKey: "completed",
      columnLabel: "Completed",
      columnStyle: { width: "140px" },
      cellRender: (opt: ApiStudy) => {
        // Use job.complete_ts as primary source
        const completedTs = opt.job?.complete_ts || opt.completed_ts;
        return completedTs ? dayjs(completedTs).format("YYYY-MM-DD HH:mm") : "N/A";
      },
    },
    {
      columnKey: "exchange_symbol",
      columnLabel: "Exchange / Symbol",
      columnStyle: { width: "180px" },
      cellRender: (opt: ApiStudy) => {
        if (!opt.job?.exchange_name || !opt.job?.symbol_name) return "N/A";

        return (
          <div>
            <div>
              <strong>{opt.job.exchange_name}</strong>
            </div>
            <div className="text-muted small">{opt.job.symbol_name}</div>
          </div>
        );
      },
    },
    {
      columnKey: "date_range",
      columnLabel: "Date Range",
      columnStyle: { width: "200px" },
      cellRender: (opt: ApiStudy) => {
        if (!opt.job?.from_ts || !opt.job?.end_ts) return "N/A";

        const fromDate = dayjs(opt.job.from_ts).format("YYYY-MM-DD");
        const endDate = dayjs(opt.job.end_ts).format("YYYY-MM-DD");
        return `${fromDate} → ${endDate}`;
      },
    },
    {
      columnKey: "actions",
      columnLabel: "",
      columnStyle: { width: "50px", textAlign: "right" as const },
      cellRender: (opt: ApiStudy) => (
        <CButton color="danger" onClick={() => handleDelete(opt)} size="sm" title="Delete">
          🗑
        </CButton>
      ),
    },
  ];

  const handleNewOptimization = () => {
    navigate("/optimization/new");
  };

  return (
    <CCard>
      <CCardBody>
        <PaginationControls
          itemLabel="optimizations"
          currentPage={pagination.page}
          totalItems={optimizations.length}
          perPage={pagination.perPage}
          onPerPageChange={setPerPage}
          onPrevPage={prevPage}
          onNextPage={nextPage}
          onNextRequest={fetchNextPage}
          isLoading={loading}
          hasMore={hasMore}
          notice={stalled ? PAGINATION_STALLED_MESSAGE : undefined}
          defaultPageSize={defaultPageSize}
          disabled={loading}
          addon={
            <CButton color="success" size="sm" onClick={handleNewOptimization} disabled={loading}>
              + New Optimization
            </CButton>
          }
        />

        <CDataTable
          columns={optimizationTableColumns}
          items={pageOptimizations}
          striped
          small
          hover
          responsive
          loading={loading}
        />
      </CCardBody>
    </CCard>
  );
};
